import React, { useEffect, useState } from 'react';
import { Gavel, Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const field = 'w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-gray-200';

function DecisionForm({ claim, token, onDone }) {
  const [f, setF] = useState({ decision: 'reduce', amount: '', reason: '', tx: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const submit = async () => {
    setBusy(true); setErr(null);
    const cents = f.decision === 'reject' ? 0 : f.decision === 'confirm' ? claim.harm_cents : Math.round(Number(f.amount) * 100);
    const r = await fetch(`${API}/api/admin/binding/claims/${claim.id}/decision`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ decision: f.decision, approved_cents: cents, reason: f.reason, tx_hash: f.tx.trim() || null }),
    });
    const body = await r.json();
    setBusy(false);
    if (!r.ok) return setErr(typeof body.detail === 'string' ? body.detail : 'Check the fields and try again.');
    onDone();
  };
  return (
    <div className="rounded-xl border border-white/10 p-4 space-y-2" data-testid={`decision-form-${claim.id}`}>
      <div className="text-xs text-gray-400">Claim #{claim.id} . {claim.status} . harm claimed ${(claim.harm_cents / 100).toFixed(2)}</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <select value={f.decision} onChange={(e) => setF({ ...f, decision: e.target.value })} className={field} data-testid={`decision-type-${claim.id}`}>
          <option value="confirm">Confirm in full</option><option value="reduce">Reduce</option><option value="reject">Reject</option>
        </select>
        {f.decision === 'reduce' && <input type="number" step="0.01" placeholder="Approved USD" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className={field} data-testid={`decision-amount-${claim.id}`} />}
        <input placeholder="Decision tx hash (optional)" value={f.tx} onChange={(e) => setF({ ...f, tx: e.target.value })} className={field} data-testid={`decision-tx-${claim.id}`} />
      </div>
      <textarea rows={3} placeholder="Written reason (public, at least 20 characters). Cite the evidence standard sections relied on." value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} className={field} data-testid={`decision-reason-${claim.id}`} />
      <button type="button" onClick={submit} disabled={busy || f.reason.trim().length < 20} className="inline-flex items-center gap-2 rounded-full bg-red-500 hover:bg-red-600 disabled:opacity-40 px-4 py-2 text-xs font-semibold text-white" data-testid={`decision-submit-${claim.id}`}>
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Gavel className="w-3.5 h-3.5" />} Record decision (permanent)
      </button>
      {err && <p className="text-xs text-red-400" data-testid={`decision-error-${claim.id}`}>{err}</p>}
    </div>
  );
}

export function ClaimDecisionPanel() {
  const { token } = useAuth();
  const [claims, setClaims] = useState(null);
  const load = () => fetch(`${API}/api/binding/claims`, { cache: 'no-store' }).then((r) => r.json()).then((d) => setClaims(d.enabled ? d.claims : []));
  useEffect(() => { load(); }, []);
  if (!claims) return null;
  const pending = claims.filter((c) => ['Undefended', 'Slashed', 'Rejected'].includes(c.status) && !c.decision);
  return (
    <div data-testid="claim-decision-panel">
      <div className="flex items-center gap-2 mb-3"><Gavel className="w-4 h-4 text-red-300" /><h3 className="text-sm font-semibold text-gray-300">Trigger 4 arbiter decisions</h3></div>
      {pending.length === 0
        ? <p className="text-xs text-gray-500" data-testid="decision-none">No claims awaiting a decision. Claims appear here once their 72-hour defense window closes undefended.</p>
        : <div className="space-y-3">{pending.map((c) => <DecisionForm key={c.id} claim={c} token={token} onDone={load} />)}</div>}
    </div>
  );
}
